import { NavigationProp, RouteProp, useFocusEffect, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData, TouchableOpacity } from 'react-native';
import { CRMParamList } from '../../../typings/Params';
import { crmService } from '../../../services/central-api/crm';
import MyDataTable from '../../../components/MyDataTable';
import { Pagination } from '../../../components/Pagination';
import { translate } from '../../../services/translate';
import moment from 'moment';
import MyAppText from '../../../components/MyAppText';
import { useAuth } from '../../../context/Auth';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import FilterInput from '../../../components/Filter/components/FilterInput';
import Filter from './Filters';
import { useCurrentApp } from '../../../context/CurrentApp';
import CameraInformationModal from './CameraInformationModal';
import { removeDeletedDateFromSerialNo } from '../../../utils/removeDeletedDateFromSerialNo';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import CustomButton from '../../../components/CustomButton';
import CameraHistoryReportModal from './CameraHistoryReportModal';

function CameraHistory() {
    const route = useRoute<RouteProp<CRMParamList, 'CameraHistory' | 'OfflineEvents'>>();
    const navigation = useNavigation<NavigationProp<CRMParamList>>();
    const { isParentApp } = useCurrentApp();

    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useState<CRMParamList['CameraHistory'] | CRMParamList['OfflineEvents']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });

    const [camera, setCamera] = useState<Camera>();

    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [selectedCameraHistory, setSelectedCameraHistory] = useState<CameraHistoryPage>();

    const [reportModalIsVisible, setReportModalIsVisible] = useState(false);
    const [cameraInformationModalIsVisible, setCameraInformationModalIsVisible] = useState(false);
    const isFocused = useIsFocused();

    const { data, isLoading } = useQuery({
        queryKey: ['CameraHistory', filters],
        queryFn: async () => {
            if (route.params.id) {
                return crmService.getCameraHistoryPaginated(route.params.id, filters);
            } else {
                return crmService.getOfflineHistoryPaginated(filters);
            }
        },
        enabled: !!authenticatedUser && isFocused,
    });


    async function getCamera(cameraId: string) {
        const camera = await crmService.getCamera(cameraId);
        setCamera(camera);
    }

    useFocusEffect(useCallback(() => {
        if (isFocused && route.params.id) {
            getCamera(route.params.id);
        }
    }, [isFocused, route.params.id]));

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter,
                page: 0,
            }));
        }
    }

    return (
        <Container>
            <Header
                title={translate('disconnectEvents')}
                goBackAction={camera ? () => navigation.canGoBack() ? navigation.goBack() : window.location.href = '/CRM/cameras' : undefined}
                actions={!camera && isParentApp && <CustomButton
                    text={translate('downloadReport')}
                    onPress={() => {
                        setReportModalIsVisible(true);
                    }} />
                }
            />
            <Card>

                <DataTableHeader
                    title={translate('generatedTickets')}
                    zIndex={2}
                    filterInput={!camera ? <FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    /> : undefined}
                    filter={<Filter
                        authenticatedUser={authenticatedUser}
                        filters={filters}
                        setFilters={setFilters}
                        textFilter={textFilter}
                        setTextFilter={setTextFilter}
                        showTextFilter={!camera}
                    />}
                />

                <DataTableContainer
                    dataTable={<MyDataTable
                        progressPending={isLoading}
                        sortServer={true}
                        onRowClicked={(row) => {
                            if (route.params.id) {
                                return navigation.navigate('TicketCameraDetail', { id: row.id });
                            }
                            navigation.navigate('TicketDetail', { id: row.id });
                        }}
                        pointerOnHover
                        onSort={(column, direction) => {
                            navigation.setParams({
                                ...filters,
                                orderBy: column.sortField,
                                orderDirection: direction,
                                page: 1,
                            });

                            setFilters({
                                ...filters,
                                orderBy: column.sortField,
                                orderDirection: direction,
                                page: 0,
                            });
                        }}
                        columns={[{
                            name: 'ID',
                            selector: row => row.id,
                            grow: 0
                        }, {
                            name: translate('company'),
                            selector: row => row.camera.company?.name || '',
                            grow: 2,
                            wrap: true,
                            omit: !isParentApp
                        }, {
                            name: translate('camera'),
                            cell: row => <TouchableOpacity
                                onPress={() => {
                                    setSelectedCameraHistory(row);
                                    setCameraInformationModalIsVisible(true);
                                }}
                            >
                                <MyAppText style={{ textDecorationLine: 'underline' }}>{row.camera.serialNo ? removeDeletedDateFromSerialNo(row.camera.serialNo) : row.camera.title}</MyAppText>
                            </TouchableOpacity>,

                            grow: 2,
                            wrap: true,
                            omit: !!route.params.id,
                        }, {
                            name: translate('beginIn'),
                            selector: row => `${moment(row.start).format('DD/MM/YYYY HH:mm:ss')}`,
                            wrap: true,
                            sortable: true,
                            sortField: 'start',
                        }, {
                            name: translate('endIn'),
                            selector: row => row.finish ? `${moment(row.finish).format('DD/MM/YYYY HH:mm:ss')}` : '',
                            wrap: true
                        }, {
                            name: translate('duration'),
                            selector: row => {
                                const finish = row.finish ? moment(row.finish) : moment();
                                const start = moment(row.start);

                                const totalSeconds = Math.max(0, finish.diff(start, 'seconds'));

                                const hours = Math.floor(totalSeconds / 3600);
                                const minutes = Math.floor((totalSeconds % 3600) / 60);
                                const seconds = totalSeconds % 60;

                                const pad = (n: number) => String(n).padStart(2, '0');

                                return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
                            },
                            wrap: true,
                            sortable: true,
                            sortField: 'duration',
                        }, {
                            cell: row => {

                                if (!authenticatedUser?.isAdmin && !authenticatedUser?.permissions.crm_module_access) {
                                    return <></>;
                                }

                                const actions: Action[] = [{
                                    content: translate('viewInformation'),
                                    onPress: () => {
                                        if (route.params.id) {
                                            return navigation.navigate('TicketCameraDetail', { id: row.id });
                                        }
                                        navigation.navigate('TicketDetail', { id: row.id });
                                    }
                                }];

                                if (!row.camera.deletedAt) {
                                    actions.push({
                                        content: translate('openCameraConfigurationOnNewTab'),
                                        onPress: () => {
                                            window.open(`/CRM/cameras/details/${row.camera.id}`);
                                        }
                                    });

                                    actions.push({
                                        content: translate('watchCameraOnNewTab'),
                                        onPress: () => {
                                            window.open(`/cameras/map/${row.camera.id}`);
                                        },
                                    });
                                }

                                return <DropdownButton
                                    actions={actions}
                                    size={320}
                                    offsetX={-168}
                                />;
                            },
                            wrap: true,
                            button: true
                        }]}
                        data={data?.rows ?? []}
                    />}
                    pagination={<Pagination
                        totalItems={data?.count ?? 0}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />

            </Card>

            <CameraInformationModal
                isVisible={cameraInformationModalIsVisible}
                setIsVisible={setCameraInformationModalIsVisible}
                cameraHistory={selectedCameraHistory}
            />

            <CameraHistoryReportModal
                isVisible={reportModalIsVisible}
                setIsVisible={setReportModalIsVisible}
            />
        </Container>
    );
}

export default CameraHistory;
